import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd/lib/grid';
import Checkbox from 'antd/lib/checkbox';
import Text from 'antd/lib/typography/Text';
import Slider from 'antd/lib/slider';
import Select from 'antd/lib/select';
import InputNumber from 'antd/lib/input-number';

import { CombinedState } from 'reducers';
import { ImageFilterAlias, hasFilter } from 'utils/image-processing';
import DepthVisualizationFilter, { DepthVisualizationOptions, createDepthVisualizationFilter } from './depth-visualization-filter';
import {
    enableImageFilter,
    disableImageFilter,
} from 'actions/settings-actions';

export default function DepthVisualization(): JSX.Element {
    const dispatch = useDispatch();
    const filters = useSelector((state: CombinedState) => state.settings.imageFilters);
    const depthFilter = hasFilter(filters, ImageFilterAlias.DEPTH_VISUALIZATION);

    const [options, setOptions] = useState<DepthVisualizationOptions>({
        showDepth: false,
        showInvalidDepth: false,
        opacity: 0.5,
        colorMap: 'jet',
        minDepth: 0,
        maxDepth: 4000,
    });

    const updateFilter = useCallback((newOptions: Partial<DepthVisualizationOptions>) => {
        const updatedOptions = { ...options, ...newOptions };
        setOptions(updatedOptions);
        if (depthFilter) {
            dispatch(enableImageFilter(depthFilter, updatedOptions));
        } else {
            dispatch(enableImageFilter(createDepthVisualizationFilter(updatedOptions)));
        }
    }, [options, depthFilter, dispatch]);

    const onChangeShowDepth = useCallback((checked: boolean): void => {
        const updatedOptions = { ...options, showDepth: checked };
        setOptions(updatedOptions);
        if (checked) {
            updateFilter({ showDepth: checked });
        } else {
            dispatch(disableImageFilter(ImageFilterAlias.DEPTH_VISUALIZATION));
        }
    }, [updateFilter, dispatch]);

    useEffect(() => {
        if (depthFilter) {
            const filterOptions = (depthFilter.modifier as DepthVisualizationFilter).options;
            setOptions(filterOptions);
        }
    }, [depthFilter]);

    return (
        <div className='cvat-depth-visualization-content'>
            <Text strong>Depth visualization</Text>
            <hr />
            <Row justify='space-between' align='middle'>
                <Col>
                    <Checkbox
                        checked={options.showDepth}
                        onChange={(e) => onChangeShowDepth(e.target.checked)}
                    >
                        Show depth overlay
                    </Checkbox>
                </Col>
                <Col>
                    <Checkbox
                        checked={options.showInvalidDepth}
                        onChange={(e) => updateFilter({ showInvalidDepth: e.target.checked })}
                    >
                        Show invalid pixels
                    </Checkbox>
                </Col>
            </Row>
            <Row align='middle'>
                <Col span={6}>
                    <Text className='cvat-text-color'>Opacity</Text>
                </Col>
                <Col span={18}>
                    <Slider
                        min={0}
                        max={1}
                        step={0.01}
                        value={options.opacity}
                        onChange={(value) => updateFilter({ opacity: value })}
                    />
                </Col>
            </Row>
            <Row align='middle'>
                <Col span={6}>
                    <Text className='cvat-text-color'>Color Map</Text>
                </Col>
                <Col span={18}>
                    <Select
                        value={options.colorMap}
                        onChange={(value) => updateFilter({ colorMap: value })}
                        className='cvat-depth-colormap-select'
                    >
                        <Select.Option value='jet'>Jet</Select.Option>
                        <Select.Option value='viridis'>Viridis</Select.Option>
                        <Select.Option value='greys'>Grayscale</Select.Option>
                    </Select>
                </Col>
            </Row>
            <Row align='middle'>
                <Col span={6}>
                    <Text className='cvat-text-color'>Min Depth</Text>
                </Col>
                <Col span={18}>
                    <InputNumber
                        min={0}
                        max={options.maxDepth}
                        value={options.minDepth}
                        onChange={(value) => updateFilter({ minDepth: value as number })}
                        className='cvat-depth-range-input'
                    />
                </Col>
            </Row>
            <Row align='middle'>
                <Col span={6}>
                    <Text className='cvat-text-color'>Max Depth</Text>
                </Col>
                <Col span={18}>
                    <InputNumber
                        min={options.minDepth}
                        value={options.maxDepth}
                        onChange={(value) => updateFilter({ maxDepth: value as number })}
                        className='cvat-depth-range-input'
                    />
                </Col>
            </Row>
        </div>
    );
}